import React, { useRef, useState } from "react";
import { HiX, HiCheck } from "react-icons/hi";

interface AddNiggleLogFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (niggleLog: {
    impact: number;
    notes: string;
    pain: number;
    linkedTrainingLogID?: string;
    logDate?: string;
  }) => void;
  dateID: string;
  editMode?: boolean;
  initialData?: {
    impact: number;
    notes: string;
    pain: number;
    linkedTrainingLogID?: string;
  };
  logId?: string;
  linkedTrainingLogID?: string;
}

const AddNiggleLogForm: React.FC<AddNiggleLogFormProps> = ({
  open,
  onClose,
  onSubmit,
  dateID,
  editMode = false,
  initialData,
  logId,
  linkedTrainingLogID,
}) => {
  const [pain, setPain] = useState(initialData?.pain || 1);
  const [impact, setImpact] = useState(initialData?.impact || 1);
  const [notes, setNotes] = useState(initialData?.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  // Convert between a YYYYMMDD dateID and a YYYY-MM-DD <input type="date"> value
  const dateIDToInputValue = (id: string): string => {
    if (!id || id.length < 8) return "";
    return `${id.substring(0, 4)}-${id.substring(4, 6)}-${id.substring(6, 8)}`;
  };
  const inputValueToDateID = (val: string): string => val.replace(/-/g, "");

  const [dateInput, setDateInput] = useState<string>(
    dateIDToInputValue(dateID),
  );
  const dateInputRef = useRef<HTMLInputElement>(null);

  const openDatePicker = () => {
    const el = dateInputRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
      } else {
        el.focus();
      }
    } catch {
      el.focus();
    }
  };

  const formatDate = (dateID: string) => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6));
    const day = parseInt(dateID.substring(6, 8));
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const niggleLogData: {
        pain: number;
        impact: number;
        notes: string;
        linkedTrainingLogID?: string;
        logDate?: string;
      } = {
        pain,
        impact,
        notes: notes.trim(),
        logDate: inputValueToDateID(dateInput) || dateID,
      };

      // Only include linkedTrainingLogID if it has a value (Firestore doesn't accept undefined)
      if (linkedTrainingLogID) {
        niggleLogData.linkedTrainingLogID = linkedTrainingLogID;
      }

      await onSubmit(niggleLogData);
      handleClose();
    } catch (error) {
      console.error("Error adding niggle log:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (editMode && initialData) {
      setPain(initialData.pain);
      setImpact(initialData.impact);
      setNotes(initialData.notes);
    } else {
      setPain(1);
      setImpact(1);
      setNotes("");
    }
    onClose();
  };

  // Update form values when initial data changes
  React.useEffect(() => {
    if (editMode && initialData) {
      setPain(initialData.pain);
      setImpact(initialData.impact);
      setNotes(initialData.notes);
    } else if (!editMode) {
      setPain(1);
      setImpact(1);
      setNotes("");
    }
    setDateInput(dateIDToInputValue(dateID));
  }, [editMode, initialData, open, dateID]);

  const getSliderBackground = (value: number): string => {
    const percent = ((value - 1) / 9) * 100; // converts 1–10 to 0–100%

    const colorScale = [
      "#fed7aa", // 1 - light orange
      "#fdba74", // 2
      "#fb923c", // 3
      "#f97316", // 4 - orange
      "#ea580c", // 5 - dark orange
      "#c2410c", // 6
      "#b91c1c", // 7 - entering red
      "#ef4444", // 8
      "#dc2626", // 9
      "#b91c1c", // 10 - deep red
    ];

    const color = colorScale[value - 1] || "#dc2626";

    return `linear-gradient(to right, ${color} 0%, ${color} ${percent}%, #e5e7eb ${percent}%, #e5e7eb 100%)`;
  };

  const getPainResponse = (painLevel: number): string => {
    const responses = [
      "No pain at all",
      "Very mild discomfort",
      "Mild pain",
      "Noticeable pain",
      "Moderate pain",
      "Moderately strong pain",
      "Strong pain",
      "Very strong pain",
      "Extremely strong pain",
      "Unbearable pain",
    ];
    return responses[painLevel - 1] || "Select a response...";
  };

  const getImpactResponse = (impactLevel: number): string => {
    const responses = [
      "No impact on performance",
      "Barely noticeable impact",
      "Slight impact on performance",
      "Noticeable impact",
      "Moderate impact on performance",
      "Significant impact",
      "Major impact on performance",
      "Severe impact",
      "Extremely limiting",
      "Cannot perform at all",
    ];
    return responses[impactLevel - 1] || "Select a response...";
  };

  if (!open) return null;

  const displayDateID = inputValueToDateID(dateInput) || dateID;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm overflow-y-auto"
      style={{ transform: "translateZ(0)" }}
    >
      <div className="flex min-h-screen md:items-center md:justify-center items-start justify-center">
        <div className="w-full max-w-2xl mx-auto overflow-hidden rounded-none md:rounded-md bg-base-100 text-left align-middle shadow-xl min-h-screen md:min-h-0 md:max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-start p-6 flex-shrink-0">
            <div>
              <div className="flex items-center gap-1">
                <h2 className="text-lg font-grotesk">
                  {editMode ? "Edit Niggle" : "Add Niggle"}
                </h2>
                <button
                  onClick={() => setShowInfoDialog(true)}
                  className="p-1 hover:bg-base-200 rounded-full transition-colors"
                  type="button"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-base-content/50 hover:text-base-content/80 text-xs font-bold">
                    ?
                  </span>
                </button>
              </div>
              {/* Clickable date — opens a native date picker */}
              <div className="relative mt-1">
                <button
                  type="button"
                  onClick={openDatePicker}
                  className="flex items-center gap-1.5 text-sm text-base-content/60 hover:text-base-content transition-colors"
                >
                  <span className="font-medium underline decoration-dotted underline-offset-4">
                    {formatDate(displayDateID)}
                  </span>
                </button>
                <input
                  ref={dateInputRef}
                  type="date"
                  value={dateInput}
                  onChange={(e) => setDateInput(e.target.value)}
                  className="absolute left-0 top-6 w-0 h-0 opacity-0 pointer-events-none"
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>
              {linkedTrainingLogID && (
                <p className="text-xs text-blue-600 mt-1 font-medium">
                  📝 Linked to training session
                </p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="bg-base-200 p-2 rounded-full"
            >
              <HiX className="w-5 h-5 text-base-content/80" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div
            className="flex-1 overflow-y-auto space-y-6"
            style={{
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              transform: "translateZ(0)",
              maxHeight: "calc(100vh - 200px)",
              minHeight: "0",
            }}
          >
            {/* Pain Level */}
            <div className="mb-6 pt-4">
              <div className="rounded-lg p-8 py-2">
                <p className="text-sm text-base-content/50 font-medium mb-2 font-inter">
                  Pain Level
                </p>
                <h3 className="text-lg font-semibold mb-4 leading-relaxed font-outfit">
                  How much pain are you experiencing?
                </h3>
                <div className="p-4 mb-4 bg-base-200 text-base-content rounded-lg">
                  <p className="font-semibold font-inter">
                    {getPainResponse(pain)}
                  </p>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={pain}
                  onChange={(e) => setPain(Number(e.target.value))}
                  className="custom-slider w-full mb-4"
                  step="1"
                  style={{ background: getSliderBackground(pain) }}
                />
                <div className="flex justify-between px-2.5 mt-2 text-xs text-base-content/60">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <span key={num}>{num}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Impact Level */}
            <div className="mb-6">
              <div className="rounded-lg p-8 py-2">
                <p className="text-sm text-base-content/50 font-medium mb-2 font-inter">
                  Impact on Performance
                </p>
                <h3 className="text-lg font-semibold mb-4 leading-relaxed font-outfit">
                  How much does this affect your performance?
                </h3>
                <div className="p-4 mb-4 bg-base-200 text-base-content rounded-lg">
                  <p className="font-semibold font-inter">
                    {getImpactResponse(impact)}
                  </p>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={impact}
                  onChange={(e) => setImpact(Number(e.target.value))}
                  className="custom-slider w-full mb-4"
                  step="1"
                  style={{ background: getSliderBackground(impact) }}
                />
                <div className="flex justify-between px-2.5 mt-2 text-xs text-base-content/60">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <span key={num}>{num}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="form-control p-0 sm:px-8 px-0 pt-4">
              <textarea
                className="p-4 px-8 sm:px-4 bg-base-200 text-[16px] sm:text-sm h-40 resize-none sm:rounded-md focus:outline-none focus:ring-0 rounded-none"
                style={{ lineHeight: "1.1" }}
                placeholder="Describe the location, when it started, what makes it better/worse, etc..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {/* Footer — floats over the scrolling content with a base→transparent fade */}
          <div className="px-5 pb-5 pt-28 -mt-24 flex-shrink-0 relative z-50 flex items-center justify-end gap-3 bg-gradient-to-t from-base-100 from-30% to-transparent pointer-events-none">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              aria-label={editMode ? "Update niggle" : "Log niggle"}
              title={editMode ? "Update niggle" : "Log niggle"}
              className="pointer-events-auto w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                <HiCheck className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Information Dialog */}
      {showInfoDialog && (
        <div
          className="fixed inset-0 z-[10000] bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowInfoDialog(false)}
        >
          <div
            className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold font-grotesk text-base-content">
                  <span className="font-light">Tracking</span> Niggles
                </h3>
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="p-1 hover:bg-base-200 rounded-full transition-colors"
                >
                  <HiX className="w-5 h-5 text-base-content/60" />
                </button>
              </div>

              <div className="space-y-4 text-sm text-base-content/80">
                <div>
                  <p className="font-inter">
                    A niggle is a minor injury, discomfort, or pain that isn't
                    severe enough to stop you from training but could develop
                    into something more serious if ignored. Tracking niggles
                    helps you catch potential injuries early.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Pain Level
                  </h4>
                  <p className="font-inter">
                    Rating your pain level (1-10) helps you monitor whether the
                    niggle is improving, staying the same, or getting worse over
                    time. Persistent or increasing pain is a warning sign that
                    requires attention.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Impact on Training
                  </h4>
                  <p className="font-inter">
                    Understanding how much a niggle affects your training (1-10)
                    helps you make informed decisions about when to push
                    through, when to modify training, and when to rest
                    completely.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Pattern Recognition
                  </h4>
                  <p className="font-inter">
                    By logging niggles consistently, you can identify patterns
                    related to specific training types, volumes, or intensities.
                    The daily insight and Wizard function helps you highlight
                    and therefore adjust your training to prevent recurring
                    issues.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Early Intervention
                  </h4>
                  <p className="font-inter">
                    Tracking niggles allows you to intervene early with rest,
                    recovery methods, or professional help before a minor issue
                    becomes a major injury that sidelines you for longer.
                  </p>
                </div>

                <div className="pt-2 border-t border-base-200">
                  <p className="text-xs text-base-content/80 font-inter italic">
                    Most serious injuries start as niggles. Tracking them is
                    your first line of defence against long-term setbacks.
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="w-full bg-neutral text-white py-3 px-6 font-grotesk font-bold rounded-md hover:bg-neutral transition-colors"
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddNiggleLogForm;
